import {FC, useEffect, useState, useMemo} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Button} from '../../../../components/Button/Button'
import {Input} from '../../../../components/Input/Input'
import {Loading} from '../../../../components/Loading/Loading'
import {useNavigate} from 'react-router-dom'
import {GET, POST} from '../../../../services/api'
import Swal from 'sweetalert2'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import useModals from '../../../../hooks/useModals'
import uuid from 'react-uuid'
import {EditorQuill} from '../../../../components/EditorQuill/EditorQuill'

// Modals
import {ServiceRequestModal} from '../../../../components/ServiceResquests/Modal'
import {IconPickerModal} from '../../ContentStructured/ServiceResquests/components/IconPickerModal'
import {GridSelectionModal} from '../../ContentStructured/ServiceResquests/components/GridSelectionModal'

// Types - Seguindo exatamente o padrão do config.tsx mas com períodos
type TimePeriod = {
  id: string
  startTime: string
  endTime: string
  interval: number
}

type BookingDayType = {
  day: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'
  able: boolean
  bookingHours: string[]
  periods?: TimePeriod[]
}

type DayType = {
  name: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'
  nick: 'sun' | 'mon' | 'tue' | 'wed' | 'thur' | 'fri' | 'sat'
  able: boolean
}

type GridHotel = {
  id: number
  title: string
  status: boolean
  description?: string
  capacity?: number
  multiple?: boolean
}

const BookingHotelCreate: FC = () => {
  const navigate = useNavigate()
  const {showWrapperModal, closeWrapperModal} = useModals()

  // Loading states
  const [loading, setLoading] = useState(false)

  // Form fields - Seguindo padrão das Activities
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState(true)
  const [image, setImage] = useState<File | null>(null)

  // Icon picker - Seguindo padrão do ServiceRequests
  const [icons, setIcons] = useState<any[]>([])
  const [sessionIcon, setSessionIcon] = useState('')
  const [sessionIconId, setSessionIconId] = useState<number | null>(null)
  const [sessionIconSvg, setSessionIconSvg] = useState('')
  const [selectedIcon, setSelectedIcon] = useState('')
  const [selectedIconId, setSelectedIconId] = useState<number | null>(null)
  const [showIconModal, setShowIconModal] = useState(false)

  // Day configuration
  const [daySelected, setDaySelected] = useState<BookingDayType | null>(null)
  const [bookingDays, setBookingsDays] = useState<BookingDayType[]>([])
  const [days, setDays] = useState<DayType[]>([
    {name: 'sunday', nick: 'sun', able: false},
    {name: 'monday', nick: 'mon', able: false},
    {name: 'tuesday', nick: 'tue', able: false},
    {name: 'wednesday', nick: 'wed', able: false},
    {name: 'thursday', nick: 'thur', able: false},
    {name: 'friday', nick: 'fri', able: false},
    {name: 'saturday', nick: 'sat', able: false},
  ])
  const [showDayModal, setShowDayModal] = useState(false)

  // Grid selection
  const [selectedGridIds, setSelectedGridIds] = useState<number[]>([])
  const [gridHotels, setGridHotels] = useState<GridHotel[]>([])
  const [loadingGrids, setLoadingGrids] = useState(false)
  const [showGridModal, setShowGridModal] = useState(false)
  const [gridSearchQuery, setGridSearchQuery] = useState('')

  // Time periods for day configuration
  const [tempPeriods, setTempPeriods] = useState<TimePeriod[]>([])

  // Utility: check if periods overlap
  const periodsOverlap = (periods: TimePeriod[]) => {
    if (!periods || periods.length <= 1) return false
    const toMinutes = (time: string) => {
      const parts = time.split(':')
      const h = parseInt(parts[0] || '0', 10)
      const m = parseInt(parts[1] || '0', 10)
      return h * 60 + m
    }

    const sorted = [...periods].sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime))
    for (let i = 1; i < sorted.length; i++) {
      const prevEnd = toMinutes(sorted[i - 1].endTime)
      const curStart = toMinutes(sorted[i].startTime)
      // if previous end is greater than current start there's an overlap
      if (prevEnd > curStart) return true
    }
    return false
  }

  // Day names
  const dayNames = {
    sunday: String(i18n.t('booking_hotel_services.day_sunday')),
    monday: String(i18n.t('booking_hotel_services.day_monday')),
    tuesday: String(i18n.t('booking_hotel_services.day_tuesday')),
    wednesday: String(i18n.t('booking_hotel_services.day_wednesday')),
    thursday: String(i18n.t('booking_hotel_services.day_thursday')),
    friday: String(i18n.t('booking_hotel_services.day_friday')),
    saturday: String(i18n.t('booking_hotel_services.day_saturday')),
  }

  // Initialize data
  const init = async () => {
    try {
      setLoading(true)
      // Load icons
      const iconsRes = await POST('/svgwebapp/filter')
      setIcons(iconsRes?.data ?? [])

      // Load grids
      await fetchGridHotels()

      // Initialize booking days
      const initialBookingDays = days.map((day) => ({
        day: day.name,
        able: false,
        bookingHours: [],
      }))
      setBookingsDays(initialBookingDays)
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch grid hotels
  const fetchGridHotels = async () => {
    try {
      setLoadingGrids(true)
      const res = await POST('/grid-hotel-booking/filter', {idPost: null})
      setGridHotels(res.data || [])
    } catch (error) {
      console.error('Erro ao buscar grades de hotel:', error)
      setGridHotels([])
    } finally {
      setLoadingGrids(false)
    }
  }

  // Icon picker functions - Usando modal separado
  const openIconPicker = () => {
    setSelectedIcon(sessionIcon)
    setSelectedIconId(sessionIconId)
    // open the global wrapper modal (used by ModalWrapper) and our local state
    showWrapperModal(String(i18n.t('booking_hotel_services.icon_choose_modal')))
    setShowIconModal(true)
  }

  const confirmIconSelection = () => {
    if (selectedIconId != null) {
      const selected = icons.find((icon) => icon.id === selectedIconId)
      setSessionIconId(selectedIconId)
      setSessionIconSvg(selected?.svg || '')
    }
    setSessionIcon(selectedIcon || '')
    setShowIconModal(false)
    // close global wrapper just in case (ModalWrapper may already close it)
    try {
      closeWrapperModal()
    } catch (e) {}
  }

  const closeIconModal = () => {
    setSelectedIcon('')
    setSelectedIconId(null)
    setShowIconModal(false)
    // ensure global wrapper is closed as well
    try {
      closeWrapperModal()
    } catch (e) {}
  }

  // Day configuration functions - Usando períodos (manhã, tarde, noite)
  const openDayModal = (dayName: DayType['name']) => {
    const day = days.find((d) => d.name === dayName)
    if (day) {
      setDaySelected({day: dayName, able: day.able, bookingHours: []})
      // Carrega períodos existentes se houver
      const existingDay = bookingDays.find((bd) => bd.day === dayName)
      if (existingDay && existingDay.periods) {
        setTempPeriods([...existingDay.periods])
      } else {
        setTempPeriods([])
      }
      setShowDayModal(true)
    }
  }

  const addPeriod = () => {
    if (tempPeriods.length >= 3) return // Máximo 3 períodos

    const defaultTimes = [
      {start: '08:00:00', end: '12:00:00'},
      {start: '12:00:00', end: '18:00:00'},
      {start: '18:00:00', end: '23:00:00'},
    ]

    const newPeriod: TimePeriod = {
      id: uuid(),
      startTime: defaultTimes[tempPeriods.length]?.start || '08:00:00',
      endTime: defaultTimes[tempPeriods.length]?.end || '18:00:00',
      interval: 15,
    }
    setTempPeriods((prev) => [...prev, newPeriod])
  }

  const removePeriod = (periodId: string) => {
    setTempPeriods((prev) => prev.filter((p) => p.id !== periodId))
  }

  const updatePeriod = (periodId: string, field: keyof TimePeriod, value: any) => {
    setTempPeriods((prev) => prev.map((p) => (p.id === periodId ? {...p, [field]: value} : p)))
  }

  const confirmDayConfiguration = () => {
    // validate overlapping periods first
    if (periodsOverlap(tempPeriods)) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('booking_hotel_services.day_period_overlap')),
        text: String(i18n.t('booking_hotel_services.day_period_overlap')),
      })
      return
    }

    if (daySelected && tempPeriods.length > 0) {
      // Atualiza bookingDays com os períodos
      const newBookingDays = bookingDays.map((bd) => bd)
      const dayIndex = newBookingDays.findIndex((bd) => bd.day === daySelected.day)

      if (dayIndex >= 0) {
        newBookingDays[dayIndex] = {
          ...newBookingDays[dayIndex],
          able: true,
          periods: tempPeriods,
        }
      }

      setBookingsDays(newBookingDays)

      // Atualiza estado dos days
      const newDays = days.map((d) => (d.name === daySelected.day ? {...d, able: true} : d))
      setDays(newDays)
    }
    setShowDayModal(false)
    setDaySelected(null)
    setTempPeriods([])
  }

  const closeDayModal = () => {
    setShowDayModal(false)
    setDaySelected(null)
    setTempPeriods([])
  }

  // Grid selection functions - Usando ServiceRequestModal
  const openGridModal = () => {
    setGridSearchQuery('')
    setShowGridModal(true)
  }

  const toggleGridSelection = (gridId: number) => {
    setSelectedGridIds((prev) =>
      prev.includes(gridId) ? prev.filter((id) => id !== gridId) : [...prev, gridId]
    )
  }

  const confirmGridSelection = () => {
    setShowGridModal(false)
    setGridSearchQuery('')
  }

  const closeGridModal = () => {
    setShowGridModal(false)
    setGridSearchQuery('')
  }

  // Filter grids by search
  const filteredGrids = useMemo(() => {
    if (!gridSearchQuery) return gridHotels
    const query = gridSearchQuery.toLowerCase()
    return gridHotels.filter(
      (grid) =>
        grid.title.toLowerCase().includes(query) || grid.description?.toLowerCase().includes(query)
    )
  }, [gridHotels, gridSearchQuery])

  // Save booking
  const handleSave = async () => {
    try {
      setLoading(true)

      // Validation
      if (!title.trim()) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('booking_hotel_services.validation_title_required')),
          text: String(i18n.t('booking_hotel_services.validation_title_text')),
        })
        return
      }

      if (!sessionIconId) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('booking_hotel_services.validation_icon_required')),
          text: String(i18n.t('booking_hotel_services.validation_icon_text')),
        })
        return
      }

      const activeDays = days.filter((day) => day.able)
      if (activeDays.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('booking_hotel_services.validation_day_required')),
          text: String(i18n.t('booking_hotel_services.validation_day_text')),
        })
        return
      }

      let imageUrl = ''

      // Upload image if provided - Seguindo padrão das Activities
      if (image) {
        const formData = new FormData()
        formData.append('file', image)
        formData.append('nameImage', `booking-${uuid()}.jpg`)
        formData.append('originalSize', 'true')

        const imageRes = await POST('/upload-images/upload-one', formData)
        if (imageRes.success) {
          imageUrl = imageRes.image
        }
      }

      // Get booking days configuration
      const bookingDaysConfig = bookingDays.filter((day) =>
        activeDays.some((activeDay) => activeDay.name === day.day && day.able)
      )

      // Final validation: ensure no overlapping periods for any day
      for (const day of bookingDaysConfig) {
        if (day.periods && periodsOverlap(day.periods)) {
          Swal.fire({
            icon: 'warning',
            title: String(i18n.t('booking_hotel_services.day_period_overlap')),
            text: String(i18n.t('booking_hotel_services.day_period_overlap')),
          })
          return
        }
      }

      // Prepare payload
      const payload = {
        title: title.trim(),
        content: description.trim(),
        status,
        img: imageUrl || null,
        iconId: sessionIconId,
        contentHotelService: bookingDaysConfig.map((day) => ({
          day: day.day,
          able: day.able,
          periods: day.periods,
        })),
        type: 'hotel_service',
        gridIds: selectedGridIds,
      }

      // Save to API
      const res = await POST('/post', payload)

      if (res.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('booking_hotel_services.success_created')),
          text: String(i18n.t('booking_hotel_services.success_created_text')),
        }).then(() => navigate('/hospitality/context/booking_config_hotel'))
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('booking_hotel_services.error_create')),
          text: res?.message || String(i18n.t('booking_hotel_services.error_create_text')),
        })
      }
    } catch (error) {
      console.error('Erro ao salvar reserva:', error)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('booking_hotel_services.error_save')),
        text: String(i18n.t('booking_hotel_services.error_save_text')),
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {String(i18n.t('booking_hotel_services.create_title'))}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {String(i18n.t('booking_hotel_services.create_subtitle'))}
                </span>
              </h3>
            </div>

            <div className='card-body'>
              {/* Formulário principal */}
              <div className='row'>
                <div className='col-md-6'>
                  <Input
                    inputAttr={{type: 'string'}}
                    placeholder={String(i18n.t('booking_hotel_services.field_title_placeholder'))}
                    label={String(i18n.t('booking_hotel_services.field_title_label'))}
                    change={(value: string) => setTitle(value)}
                    value={title}
                    marginTop='0'
                    maxCaracteres={50}
                  />
                </div>

                <div className='col-md-6'>
                  <Input
                    inputAttr={{type: 'select'}}
                    value={status}
                    change={(val: boolean) => setStatus(val)}
                    label={String(i18n.t('booking_hotel_services.field_status_label'))}
                    options={[
                      {select: String(i18n.t('booking_hotel_services.status_active')), value: true},
                      {
                        select: String(i18n.t('booking_hotel_services.status_inactive')),
                        value: false,
                      },
                    ]}
                    marginTop='0'
                  />
                </div>
              </div>

              <div className='row mt-3'>
                <div className='col-md-12'>
                  <label className='form-label'>{i18n.t('clean_safe.description')}</label>
                  <div>
                    <EditorQuill
                      value={description}
                      change={(value: string) => setDescription(value)}
                      height={150}
                      maxLength={500}
                    />
                  </div>
                </div>

                {/* Upload de imagem - Seguindo padrão das Activities */}
                <div className='row'>
                  <Input
                    inputAttr={{type: 'image'}}
                    label={String(i18n.t('booking_hotel_services.field_image_label'))}
                    value={image}
                    change={(value: React.BaseSyntheticEvent) =>
                      setImage(value ? value.target.files[0] : null)
                    }
                  />
                </div>
              </div>

              {/* Icon Picker */}
              <div className='row mt-3'>
                <div className='col-md-6'>
                  <label className='form-label fw-bold'>
                    {String(i18n.t('booking_hotel_services.field_icon_label'))}
                  </label>
                  <div className='d-flex align-items-center gap-3'>
                    <Button
                      text={String(i18n.t('booking_hotel_services.icon_choose'))}
                      color='outline-primary'
                      size='small'
                      click={openIconPicker}
                    />
                    {sessionIconSvg && (
                      <div
                        dangerouslySetInnerHTML={{__html: sessionIconSvg}}
                        style={{width: 24, height: 24}}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className='border-bottom border-gray-300 my-8'></div>

              {/* Days Configuration - EXATAMENTE como no config.tsx */}
              <div className='mb-4'>
                <h4 className='mb-6'>
                  {String(i18n.t('booking_hotel_services.day_config_title'))}
                </h4>
                <div className='mb-3 d-flex align-items-start gap-2'>
                  <i className='bi bi-info-circle-fill text-info fs-5' />
                  <div className='text-muted small'>
                    <div>{String(i18n.t('booking_hotel_services.day_config_info_line1'))}</div>
                    <div>{String(i18n.t('booking_hotel_services.day_config_info_line2'))}</div>
                  </div>
                </div>
                <div className='d-flex flex-row align-items-start gap-4'>
                  {days.map((dayItem) => (
                    <div key={dayItem.name} className='d-flex flex-column align-items-center gap-2'>
                      <button
                        type='button'
                        className={`btn btn-sm ${dayItem.able ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => {
                          const newDays = days.map((d) => d)
                          const dayIndex = newDays.findIndex((d) => d.name === dayItem.name)
                          newDays[dayIndex].able = !newDays[dayIndex].able
                          setDays(newDays)

                          // Atualizar booking days
                          const newBookingDays = bookingDays.map((bd) => bd)
                          const bookingDayIndex = newBookingDays.findIndex(
                            (bd) => bd.day === dayItem.name
                          )
                          if (bookingDayIndex >= 0) {
                            newBookingDays[bookingDayIndex].able = newDays[dayIndex].able
                            if (!newDays[dayIndex].able) {
                              newBookingDays[bookingDayIndex].bookingHours = []
                            }
                          }
                          setBookingsDays(newBookingDays)
                        }}
                        style={{minWidth: 70}}
                      >
                        {dayNames[dayItem.name]}
                      </button>
                      <div>
                        {dayItem.able && (
                          <button
                            type='button'
                            className='btn btn-link btn-sm p-0'
                            onClick={() => openDayModal(dayItem.name)}
                          >
                            <small>{String(i18n.t('booking_hotel_services.day_configure'))}</small>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Grid Selection - Usando ServiceRequestModal */}
              <div className='mb-6'>
                <h4 className='mb-3'>{String(i18n.t('booking_hotel_services.grid_title'))}</h4>
                <div className='d-flex align-items-center gap-3 mb-3'>
                  <Button
                    text={String(i18n.t('booking_hotel_services.grid_select'))}
                    color='outline-primary'
                    size='small'
                    click={openGridModal}
                  />
                  {selectedGridIds.length > 0 && (
                    <span className='badge badge-primary'>
                      {selectedGridIds.length}{' '}
                      {String(i18n.t('booking_hotel_services.grid_selected'))}
                    </span>
                  )}
                </div>

                {/* Show selected grids as cards (seguindo padrão do modal) */}
                {selectedGridIds.length > 0 ? (
                  <div className='d-flex flex-wrap gap-3'>
                    {selectedGridIds.map((gridId) => {
                      const grid = gridHotels.find((g) => g.id === gridId)
                      return (
                        <div
                          key={gridId}
                          className='border rounded p-3 d-flex align-items-start'
                          style={{minWidth: 220, maxWidth: 320}}
                        >
                          <div className='flex-grow-1'>
                            <div className='d-flex align-items-start justify-content-between'>
                              <h6 className='mb-1'>{grid?.title || `Grade #${gridId}`}</h6>
                            </div>
                            {grid?.description && (
                              <p className='text-muted mb-1 small'>{grid.description}</p>
                            )}
                            <small className='text-muted d-block'>
                              {String(i18n.t('booking_hotel_services.grid_capacity'))}:{' '}
                              {grid?.capacity ?? 'N/A'} •{' '}
                              {String(i18n.t('booking_hotel_services.grid_multiple'))}:{' '}
                              {grid?.multiple
                                ? String(i18n.t('booking_hotel_services.grid_multiple_yes'))
                                : String(i18n.t('booking_hotel_services.grid_multiple_no'))}
                            </small>
                          </div>
                          <div className='ms-3'>
                            <span
                              className={`badge ${grid?.status ? 'badge-success' : 'badge-danger'}`}
                            >
                              {grid?.status
                                ? String(i18n.t('booking_hotel_services.grid_status_active'))
                                : String(i18n.t('booking_hotel_services.grid_status_inactive'))}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : null}
              </div>

              {/* Action Buttons */}
              <div className='d-flex justify-content-between mt-8'>
                <Button
                  text={String(i18n.t('booking_hotel_services.button_back'))}
                  color='info'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{type: 'button'}}
                  click={() => navigate(-1)}
                  btnClass='fw-bolder'
                />
                <Button
                  text={String(i18n.t('booking_hotel_services.button_save'))}
                  color='primary'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{type: 'button'}}
                  click={handleSave}
                  btnClass='fw-bolder'
                />
              </div>
            </div>
          </div>

          {/* Icon Picker Modal - Usando o IconPickerModal igual aos ServiceRequests */}
          <IconPickerModal
            isOpen={showIconModal}
            wrapperModalOpen={showIconModal}
            icons={icons}
            selectedIconId={selectedIconId}
            selectedIcon={selectedIcon}
            setSelectedIcon={setSelectedIcon}
            setSelectedIconId={setSelectedIconId}
            onClose={closeIconModal}
            onConfirm={confirmIconSelection}
          />

          {/* Day Configuration Modal */}
          {showDayModal && daySelected && (
            <ServiceRequestModal
              onCloseAction={closeDayModal}
              title={`Configurar ${dayNames[daySelected.day]}`}
              showAlert={true}
            >
              <div className='mb-4 pt-6'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <h6>{String(i18n.t('booking_hotel_services.day_periods_title'))}</h6>
                  <Button
                    text={String(i18n.t('booking_hotel_services.day_period_add'))}
                    color='primary'
                    size='small'
                    click={addPeriod}
                    disabled={tempPeriods.length >= 3}
                  />
                </div>

                <small className='text-muted d-block mt-3 mb-3'>
                  {String(i18n.t('booking_hotel_services.day_period_max'))}
                </small>

                {tempPeriods.length === 0 ? (
                  <div className='text-center text-muted py-4'>
                    {String(i18n.t('booking_hotel_services.day_period_none'))}
                  </div>
                ) : (
                  <div className='space-y-3'>
                    {tempPeriods.map((period, index) => {
                      // color accent per period: 0 -> morning (amber), 1 -> afternoon (blue), 2 -> night (purple)
                      const accent = index === 0 ? '#FFC107' : index === 1 ? '#0d6efd' : '#6f42c1'
                      return (
                        <div
                          key={period.id}
                          className='p-3 mb-3'
                          style={{
                            border: '1px solid #e6e9ef',
                            borderRadius: '0.5rem',
                            boxShadow: '0 1px 4px rgba(13,17,23,0.06)',
                            backgroundColor: '#ffffff',
                            borderLeft: `4px solid ${accent}`,
                          }}
                        >
                          <div className='d-flex justify-content-between align-items-center mb-2'>
                            <div className='d-flex align-items-center gap-3'>
                              <div
                                style={{
                                  width: 10,
                                  height: 30,
                                  backgroundColor: accent,
                                  borderRadius: 4,
                                }}
                              />
                              <h6 className='mb-0'>{`${String(
                                i18n.t('booking_hotel_services.day_period_label')
                              )} ${index + 1}`}</h6>
                            </div>
                            <button
                              type='button'
                              className='btn btn-sm btn-outline-danger'
                              onClick={() => removePeriod(period.id)}
                            >
                              {String(i18n.t('booking_hotel_services.day_period_remove'))}
                            </button>
                          </div>

                          <div className='row'>
                            <div className='col-md-12'>
                              <Input
                                inputAttr={{type: 'number'}}
                                label={String(i18n.t('booking_hotel_services.day_interval_label'))}
                                value={period.interval}
                                change={(value: number) =>
                                  updatePeriod(period.id, 'interval', value)
                                }
                                marginTop='0'
                              />
                            </div>
                          </div>

                          <div className='row mt-3'>
                            <div className='col-md-6'>
                              <Input
                                inputAttr={{type: 'time'}}
                                label={String(
                                  i18n.t('booking_hotel_services.day_start_time_label')
                                )}
                                value={period.startTime}
                                change={(value: string) =>
                                  updatePeriod(period.id, 'startTime', value)
                                }
                                marginTop='0'
                              />
                            </div>
                            <div className='col-md-6'>
                              <Input
                                inputAttr={{type: 'time'}}
                                label={String(i18n.t('booking_hotel_services.day_end_time_label'))}
                                value={period.endTime}
                                change={(value: string) =>
                                  updatePeriod(period.id, 'endTime', value)
                                }
                                marginTop='0'
                              />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className='d-flex justify-content-end gap-2 mt-4'>
                <Button
                  text={String(i18n.t('booking_hotel_services.button_cancel'))}
                  color='secondary'
                  size='small'
                  click={closeDayModal}
                />
                <Button
                  text={String(i18n.t('booking_hotel_services.button_confirm'))}
                  color='primary'
                  size='small'
                  click={confirmDayConfiguration}
                />
              </div>
            </ServiceRequestModal>
          )}

          {/* Grid Selection Modal (reutilizando o componente do projeto) */}
          <GridSelectionModal
            isOpen={showGridModal}
            grids={gridHotels as any}
            loading={loadingGrids}
            searchQuery={gridSearchQuery}
            selectedGridIds={selectedGridIds}
            filteredGrids={filteredGrids as any}
            onSearchChange={(q: string) => setGridSearchQuery(q)}
            onToggleGrid={toggleGridSelection}
            onClose={closeGridModal}
            onConfirm={confirmGridSelection}
          />
        </div>
      )}
    </>
  )
}

const BookingHotelCreatePage: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: String(i18n.t('booking_hotel_services.breadcrumb_dashboard')),
            path: 'dashboard',
            isActive: false,
          },
          {
            title: String(i18n.t('booking_hotel_services.breadcrumb_hospitality')),
            path: 'hospitality',
            isActive: false,
          },
          {
            title: String(i18n.t('booking_hotel_services.breadcrumb_hotel_reservations')),
            path: 'booking-hotel',
            isActive: false,
          },
          {
            title: String(i18n.t('booking_hotel_services.breadcrumb_create')),
            path: 'create',
            isActive: true,
          },
        ]}
      >
        {String(i18n.t('booking_hotel_services.create_title'))}
      </PageTitle>
      <BookingHotelCreate />
    </>
  )
}

export {BookingHotelCreatePage}
